import useApi from "../services/ApiService";
import { Paging } from "../models/Paging";
import {AuditLog, AuditLogPerformerType, AuditLogType} from "../models/AuditLog";
import {getDateTimeString} from "../helpers/CommonHelpers";
import { ConflictReason } from "../models/ConflictReason";

export interface AuditLogQueryParams {
  eventTypes: AuditLogType[];
  fromDate?: string;
  fromTime?: string;
  toDate?: string;
  toTime?: string;
  performerTypes?: AuditLogPerformerType[];
}

interface GetAuditLogsQueryOptions {
  params?: AuditLogQueryParams;
  pageNumber?: number;
  pageSize?: number;
  orderBy?: string;
  descending?: boolean;
}

export interface DeletionRequestTarget {
  id: string;
  name: string;
}

/** One flag a client changed: a null `from` is a raise, a null `to` a withdrawal. */
export interface PathCollisionReconcileFile {
  fileId: string;
  filename: string | null;
  filePath: string | null;
  from: ConflictReason | null;
  to: ConflictReason | null;
}

export class AuditLogsRepository {
  private api = useApi();
  
  async getAuditLogs(
    options: GetAuditLogsQueryOptions = {}
  ): Promise<{ data: AuditLog[]; paging: Paging | null }> {
    const {
      params = null,
      pageNumber = null,
      pageSize = null,
      orderBy = null,
      descending = true,
    } = options;
    
    try {
      const fromDate = getDateTimeString(params?.fromDate, params?.fromTime);
      const toDate = getDateTimeString(params?.toDate, params?.toTime);
      const response = await this.api.get("/api/AuditLogs", {
        params: {
          eventTypes: params?.eventTypes,
          performerTypes: params?.performerTypes,
          FromDate: fromDate,
          ToDate: toDate,
          PageNumber: pageNumber,
          PageSize: pageSize,
          OrderBy: orderBy,
          Descending: descending,
        },
        paramsSerializer: {
          indexes: true,
        },
      });
      const { data, headers } = response;
      let paging: Paging | null = null;
      const paginationHeader = headers["x-pagination"];
      if (paginationHeader)
        paging = JSON.parse(paginationHeader || "");

      return { data, paging };
    } catch (error) {
      throw new Error("Failed to fetch audit logs");
    }
  }

  async getDeletionRequestTargets(
    auditLogId: string,
    pageNumber = 1,
    pageSize = 100
  ): Promise<{ data: DeletionRequestTarget[]; paging: Paging | null }> {
    try {
      const response = await this.api.get<DeletionRequestTarget[]>("/api/AuditLogs/DeletionRequestTargets", {
        params: {
          AuditLogId: auditLogId,
          "Paging.PageNumber": pageNumber,
          "Paging.PageSize": pageSize,
        },
      });
      const { data, headers } = response;
      const paginationHeader = headers["x-pagination"];
      return { data, paging: paginationHeader ? JSON.parse(paginationHeader) : null };
    } catch (error) {
      throw new Error("Failed to fetch the deleted targets");
    }
  }

  async getPathCollisionReconcileFiles(auditLogId: string): Promise<PathCollisionReconcileFile[]> {
    try {
      const response = await this.api.get<PathCollisionReconcileFile[]>("/api/AuditLogs/PathCollisionReconcileFiles", {
        params: { AuditLogId: auditLogId },
      });
      return response.data;
    } catch (error) {
      throw new Error("Failed to fetch the re-checked files");
    }
  }
}
